// MarketingEngagement — the Starter-plan upsell view for a Marketing surface (PE-1651).
//
// Replaces the minimal MarketingPaywall.UpgradePrompt / UpgradeLock for non-entitled
// orgs: a value-led "here's what Marketing does for you" page per surface (Broadcasts /
// Segments / Email Templates / Settings).
//
// PURELY PRESENTATIONAL — no entitlement logic, no data fetching, no routing. Wiring
// (re-point the EntitlementGate fail branch + MarketingPaywall, pass unlockTier from
// the engine, route the CTA to /settings/billing) is a separate step. Copy + per-surface
// config live in ./marketingEngagementContent.ts.
//
// Capability-first: there is no data-insight stat band (gated fast-follow — see
// IMPLEMENTATION.md §3). Styling uses the dashboard's paper/ink/edge/sage tokens to
// match the Billing page; the deep-sage accent (#4A6B4A) mirrors MarketingPaywall.

import React from 'react';
import {
  MegaphoneIcon,
  BoltIcon,
  ChartBarIcon,
  UsersIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  Squares2X2Icon,
  SwatchIcon,
} from '@heroicons/react/24/outline';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  MARKETING_ENGAGEMENT_CONTENT,
  type BenefitIconKey,
  type MarketingSurfaceKey,
  type PreviewCard as PreviewCardData,
  type PreviewTagTone,
} from './marketingEngagementContent';

// Maps a benefit's icon key (content data) to its Heroicon. Closed over
// BenefitIconKey, so adding a key without a glyph here is a compile error.
const BENEFIT_ICONS: Record<BenefitIconKey, React.ComponentType<{ className?: string }>> = {
  megaphone: MegaphoneIcon,
  bolt: BoltIcon,
  chart: ChartBarIcon,
  users: UsersIcon,
  refresh: ArrowPathIcon,
  send: PaperAirplaneIcon,
  sparkles: SparklesIcon,
  layout: Squares2X2Icon,
  brand: SwatchIcon,
};

const TAG_TONE_CLASSES: Record<PreviewTagTone, string> = {
  campaign: 'border-sage/30 bg-sage-light text-[#4A6B4A]',
  trigger: 'border-edge-input bg-paper-inset text-ink-secondary',
  locked: 'border-edge-input bg-paper-inset text-ink-muted',
};

// Hover is a deliberate COLOR SWAP (PE-1853 QA): resting = ink bg / warm-white
// label; hover = sage bg (hover:bg-primary/90, made explicit here rather than
// relying on the base Button variant's class leaking through tailwind-merge)
// with the label flipped to ink so it stays high-contrast on the lighter bg.
const PRIMARY_BTN =
  'h-9 rounded-md bg-ink px-4 text-sm font-semibold text-paper-light hover:bg-primary/90 hover:text-ink';
const SECONDARY_BTN =
  'h-9 rounded-md border border-edge-input bg-white px-4 text-sm font-medium text-ink-secondary hover:border-sage hover:text-ink';

export interface MarketingEngagementProps {
  /** Which Marketing surface this page stands in for. */
  surface: MarketingSurfaceKey;
  /** Tier label that unlocks the surface. Wiring passes `unlockTierLabel(FEATURE.BROADCASTS)`. */
  unlockTier?: string;
  /** Org name, used only in the example-email mock (cosmetic). */
  orgName?: string;
  /** CTA handlers. Wiring routes upgrade → `/settings/billing`. No-ops until wired. */
  onUpgrade?: () => void;
  /**
   * In-app "Learn more" click handler (e.g. `history.push('/settings/billing')`).
   * Used by the Elements surface for in-dashboard navigation. Mutually exclusive
   * with `learnMoreHref` in practice; if BOTH are set, `learnMoreHref` wins (an
   * external link takes precedence over an in-app push).
   */
  onLearnMore?: () => void;
  /**
   * PE-1730 — when set, "Learn more" renders as an external anchor to this URL,
   * opened in a NEW TAB (the public Grow product page). Used by the marketing
   * upsell surfaces (broadcasts / segments / email templates / Payment Impact /
   * settings) now that a public Grow page exists. Pass `growPageUrl()` from
   * `utils/siteUrls`. Takes precedence over `onLearnMore`.
   */
  learnMoreHref?: string;
  /**
   * PE-1678 — when set, the primary CTA becomes a "Contact us" link to this
   * `mailto:` href INSTEAD of the self-serve "Upgrade" button. Used for
   * `legacy_custom` (managed-account) orgs, whose plan changes go through their
   * account team, not self-serve checkout. Must NOT point at /settings/billing.
   * When undefined, the standard "Upgrade" CTA (→ onUpgrade) renders.
   */
  contactHref?: string;
}

function Pill({ unlockTier }: { unlockTier: string }) {
  return <Badge variant="success">Available on the {unlockTier} plan</Badge>;
}

function PreviewCard({ card }: { card: PreviewCardData }) {
  return (
    <div className="rounded-lg border border-edge bg-paper-inset p-3.5">
      <div className="flex items-center justify-between gap-2.5">
        <span className="text-sm font-semibold text-ink">{card.title}</span>
        <span
          className={cn(
            'rounded-full border px-2.5 py-0.5 text-[10.5px] font-medium',
            TAG_TONE_CLASSES[card.tone],
          )}
        >
          {card.tag}
        </span>
      </div>
      <div className="mt-2.5 flex gap-[18px]">
        {card.meta.map((m) => (
          <div key={m.label}>
            <div className={cn('text-sm font-bold', m.emphasize ? 'text-[#4A6B4A]' : 'text-ink')}>
              {m.value}
            </div>
            <div className="text-[11px] text-ink-muted">{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExampleEmail({ orgName }: { orgName: string }) {
  return (
    <div>
      <div className="overflow-hidden rounded-lg border border-edge bg-white shadow-paper">
        <div className="border-b border-edge px-3.5 py-2.5 text-[11.5px] text-ink-muted">
          From: {orgName}
          <span className="mt-1 block text-[13px] font-semibold text-ink">
            You helped feed 1,200 kids this Ramadan
          </span>
        </div>
        <div className="border-b border-sage/30 bg-sage-light px-3.5 py-4 text-center text-base font-extrabold tracking-tight text-[#4A6B4A]">
          {orgName}
        </div>
        <div className="px-3.5 py-3.5 text-[12.5px] text-ink-secondary">
          <p className="mb-2">Dear Aisha,</p>
          <p className="mb-2">
            Because of you, 1,200 children had a warm meal this month. Your generosity changes lives
            — and there&apos;s still more we can do together.
          </p>
          <span className="mt-1.5 inline-block rounded-md bg-[#4A6B4A] px-4 py-2 text-[12.5px] font-semibold text-white">
            Give again →
          </span>
        </div>
      </div>
      <div className="mt-2.5 text-center text-[11.5px] text-ink-muted">
        Drafted by AI in ~30 seconds — then on-brand, ready to send.
      </div>
    </div>
  );
}

// A product screenshot framed as a browser-style window (subtle chrome bar +
// rounded border + paper shadow), scaled to the column width. Used by surfaces
// that set `content.screenshot`.
function ScreenshotFrame({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="overflow-hidden rounded-lg border border-edge bg-white shadow-paper">
      <div className="flex items-center gap-1.5 border-b border-edge bg-paper-inset px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-edge-input" />
        <span className="h-2.5 w-2.5 rounded-full bg-edge-input" />
        <span className="h-2.5 w-2.5 rounded-full bg-edge-input" />
      </div>
      <img src={src} alt={alt} loading="lazy" className="block h-auto w-full" />
    </div>
  );
}

export default function MarketingEngagement({
  surface,
  unlockTier = 'Base',
  orgName = 'Your nonprofit',
  onUpgrade,
  onLearnMore,
  learnMoreHref,
  contactHref,
}: MarketingEngagementProps) {
  const content = MARKETING_ENGAGEMENT_CONTENT[surface];

  // PE-1678 — managed-account (legacy_custom) orgs see "Contact us" (a mailto)
  // in place of the self-serve "Upgrade" button. Rendered as an <a> so it never
  // routes through /settings/billing (an acceptance criterion). The Pill above
  // it ("Available on the {tier} plan") still makes sense — the surface IS a
  // higher-tier feature; they just unlock it by talking to us, not by upgrading.
  const primaryCta = contactHref ? (
    <Button asChild className={PRIMARY_BTN}>
      <a href={contactHref}>Contact us</a>
    </Button>
  ) : (
    <Button type="button" className={PRIMARY_BTN} onClick={onUpgrade}>
      Upgrade
    </Button>
  );

  // "Learn more" affordance — rendered when EITHER target is passed:
  //   - learnMoreHref (PE-1730): the marketing surfaces (broadcasts / segments /
  //     email templates / Payment Impact / settings / settings_sms) link to the
  //     public pricing page, opened in a NEW TAB. Takes precedence.
  //   - onLearnMore: the Elements surface's in-app nav (→ /settings/billing).
  // A surface that passes neither → null (button hidden, not a dead CTA). Shared
  // by BOTH the `simple` render path and the hero layout below so the affordance
  // never silently drops on a surface that opted in.
  const learnMore = learnMoreHref ? (
    <Button asChild className={SECONDARY_BTN}>
      <a href={learnMoreHref} target="_blank" rel="noopener noreferrer">
        Learn more
      </a>
    </Button>
  ) : onLearnMore ? (
    <Button type="button" className={SECONDARY_BTN} onClick={onLearnMore}>
      Learn more
    </Button>
  ) : null;

  // SMS settings — stripped down: a one-line message + CTA, no preview / benefits.
  // `settings_sms` is the only `simple` surface left after PE-1736 gave the main
  // Settings surface the full hero. Borderless (it sits inside the Settings
  // content pane, which already frames the area); just bg-white + rounded +
  // subtle shadow.
  if (content.simple) {
    return (
      <div className="w-full py-6">
        <div className="rounded-lg bg-white p-7">
          <Pill unlockTier={unlockTier} />
          <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-ink">{content.title}</h1>
          <p className="mt-2 max-w-[48ch] text-sm text-ink-secondary">{content.pitch}</p>
          <div className="mt-5 flex gap-2.5">
            {learnMore}
            {primaryCta}
          </div>
        </div>
      </div>
    );
  }

  // "What you can do" cards — reused in both the with-screenshot (rows, left
  // half) and without-screenshot (3-col grid) layouts below.
  const benefitCards = (content.benefits ?? []).map((b) => {
    const Icon = BENEFIT_ICONS[b.icon];
    return (
      <div
        key={b.title}
        className="rounded-xl border border-edge bg-paper-light p-5 transition-colors hover:border-sage/40"
      >
        <div className="mb-3 flex items-center gap-3">
          <Icon className="h-8 w-8 flex-shrink-0 text-sage" />
          <h3 className="text-sm font-semibold text-ink">{b.title}</h3>
        </div>
        <p className="text-[13px] leading-relaxed text-ink-secondary">{b.description}</p>
      </div>
    );
  });

  // Hero value text — title + pitch + CTAs. Shared between the with-screenshot
  // (1/3 column) and without-screenshot (1/2 column) hero layouts below.
  const heroText = (
    <>
      <Pill unlockTier={unlockTier} />
      <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-ink">{content.title}</h1>
      <p className="mt-2 max-w-[48ch] text-sm text-ink-secondary">{content.pitch}</p>
      <div className="mt-5 flex gap-2.5">
        {learnMore}
        {primaryCta}
      </div>
    </>
  );

  return (
    // One standard framed white panel that fills the page container — same
    // border-edge / bg-white / shadow-sm / rounded-lg treatment the Settings and
    // Donations content panels use, so this reads as the same surface (PE-1651
    // follow-up). The hero + benefits live INSIDE this one frame rather than as
    // separate floating cards on the page background.
    // EXCEPTION — the Settings surface drops the border: the Settings content
    // pane already frames the area there, so the panel border read as a double
    // frame (same reasoning as the borderless `simple` SMS variant above).
    <div className="flex h-full min-h-0 w-full flex-1 flex-col">
      <div
        className={`flex min-h-0 flex-1 flex-col overflow-y-auto rounded-lg bg-white p-7 shadow-sm ${
          surface === 'settings' ? '' : 'border border-edge'
        }`}
      >
        {/* HERO row. With a screenshot: value text on the LEFT (2/5) + the wide
            product screenshot on the RIGHT (3/5). Without one (segments / email
            templates): 50/50 with the illustrative cards / example email. */}
        {content.screenshot ? (
          <div className="grid items-center gap-6 md:grid-cols-5">
            <div className="md:col-span-2">{heroText}</div>
            <div className="md:col-span-3">
              <ScreenshotFrame src={content.screenshot} alt={`${content.title} preview`} />
            </div>
          </div>
        ) : (
          <div className="grid items-center gap-6 md:grid-cols-2">
            <div>{heroText}</div>
            <div>
              {content.exampleEmail ? (
                <ExampleEmail orgName={orgName} />
              ) : (
                <div className="flex flex-col gap-3">
                  {content.preview?.map((card) => (
                    <PreviewCard key={card.title} card={card} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* "What you can do" — capability cards, full-width row below the hero. */}
        {benefitCards.length > 0 && (
          <>
            <div className="mt-6 text-xs font-semibold tracking-wide text-ink-label">
              What you can do
            </div>
            <div className="mt-3 grid gap-4 md:grid-cols-3">{benefitCards}</div>
          </>
        )}
      </div>
    </div>
  );
}
